import { useEffect, useRef, useState } from "react";
import { HttpStatusCode, isAxiosError } from "axios";
import { z } from "zod";

import { AppointmentDetailsCard } from "~/screens/Scheduling/PersonalInformation/components/appointment-details-card";
import { useUpdateBookingFlowMutation } from "~/services/booking-flows/actions";
import { BOOKING_FLOW_EVENTS } from "~/services/booking-flows/constants";
import { useBookingFlowEvents } from "~/services/booking-flows/hooks/use-booking-flow-events";
import { FILL_OPTION, VISIT_TYPES } from "~/services/scheduling/constants";
import type {
  AdminScheduleVisitResponse,
  VisitType,
} from "~/services/scheduling/types";
import { INSURANCE_NOT_FOUND_OPTION, type User } from "~/shared.types";
import { formatDateOfBirth } from "~/shared/utils/date-of-birth";
import { formatStoredPhoneNumber } from "~/shared/utils/format-stored-phone-number";
import { useBookingFlowStore } from "~/stores/use-booking-flow-store";
import type { AppointmentDetails } from "~/stores/use-patient-store";
import { NotificationBanner } from "~/ui/notification-banner/notification-banner";
import {
  ChooseAnotherSlotButton,
  PatientInfoForm,
  type StaffPatientInfoFieldErrors,
} from "../components/patient-info-form";
import { StepHeading } from "../components/step-heading";
import { useStaffBookVisit } from "../hooks/use-staff-book-visit";
import { buildStaffBookingPayload } from "../utils/build-staff-booking-payload";
import type { StaffPatientInfoFormData } from "../utils/staff-booking-schemas";

const SLOT_UNAVAILABLE_MESSAGES: Record<string, string> = {
  provider_not_in_network:
    "This provider is not in network with the selected insurance.",
  create_open_appointment_slot: "This slot is no longer available.",
};

const BOOKING_ERROR_MESSAGES: Record<string, string> = {
  follow_up_requires_ehr_patient:
    "Follow-up visits can only be booked for patients with an Athena ID.",
};

const PAYLOAD_TO_FORM_FIELD: Record<string, keyof StaffPatientInfoFormData> = {
  patientFirstName: "firstName",
  patientLastName: "lastName",
  patientDob: "dateOfBirth",
  patientSexAtBirth: "sexAtBirth",
  patientMobilePhoneNumber: "mobilePhone",
  patientAddressOne: "homeAddress1",
  patientAddressTwo: "homeAddress2",
  patientCity: "city",
  patientStateId: "state",
  patientZipCode: "zipCode",
  patientHowDidYouHear: "referralSource",
  bookingNotes: "bookingNotes",
  addressChanged: "fillAddressInfo",
  insuranceChanged: "fillInsuranceInfo",
  insuranceCompanyName: "insuranceCompanyName",
  insuranceRelationshipToPolicyHolder: "relationshipToPolicyHolder",
  insurancePolicyNumber: "memberId",
  insuranceGroupNumber: "groupNumber",
  insurancePolicyHolderFirstName: "policyHolderFirstName",
  insurancePolicyHolderLastName: "policyHolderLastName",
  insurancePolicyHolderDob: "policyHolderDateOfBirth",
  insuranceCardFront: "insuranceCardFront",
  insuranceCardBack: "insuranceCardBack",
};

type BookingError =
  | { type: "slotUnavailable"; message: string }
  | { type: "fields"; messages: string[] }
  | { type: "blocking"; message: string }
  | { type: "generic"; message: string };

const apiErrorSchema = z.object({
  error: z.object({
    code: z.string().optional(),
    message: z.string().optional(),
    fields: z.record(z.string(), z.array(z.string())).optional(),
  }),
});

const isServerError = (error: unknown) =>
  isAxiosError(error) &&
  (error.response?.status ?? 0) >= HttpStatusCode.InternalServerError;

const parseBookingError = (
  error: unknown,
): {
  bookingError: BookingError | null;
  fieldErrors: StaffPatientInfoFieldErrors;
} => {
  const body = apiErrorSchema.safeParse(
    isAxiosError(error) ? error.response?.data : undefined,
  );
  const apiError = body.success ? body.data.error : undefined;
  const slotUnavailableMessage =
    apiError?.code && SLOT_UNAVAILABLE_MESSAGES[apiError.code];

  const blockingMessage =
    apiError?.code && BOOKING_ERROR_MESSAGES[apiError.code];

  if (blockingMessage) {
    return {
      bookingError: { type: "blocking", message: blockingMessage },
      fieldErrors: {},
    };
  }

  if (slotUnavailableMessage) {
    return {
      bookingError: {
        type: "slotUnavailable",
        message: slotUnavailableMessage,
      },
      fieldErrors: {},
    };
  }

  const fieldErrors: StaffPatientInfoFieldErrors = {};
  const unmappedMessages: string[] = [];

  Object.entries(apiError?.fields ?? {}).forEach(([key, messages]) => {
    const field = PAYLOAD_TO_FORM_FIELD[key];
    const message = messages[0];
    if (!message) return;

    if (field) {
      fieldErrors[field] = message;
    } else {
      unmappedMessages.push(message);
    }
  });

  if (Object.keys(fieldErrors).length || unmappedMessages.length) {
    return {
      bookingError: unmappedMessages.length
        ? { type: "fields", messages: unmappedMessages }
        : null,
      fieldErrors,
    };
  }

  return {
    bookingError: {
      type: "generic",
      message:
        (!isServerError(error) && apiError?.message) ||
        "The visit could not be booked. Try again.",
    },
    fieldErrors,
  };
};

const getAccountPrefill = (
  user: User | undefined,
): Partial<StaffPatientInfoFormData> => {
  if (!user || user.ehrId) return {};

  return {
    firstName: user.firstName || undefined,
    lastName: user.lastName || undefined,
    dateOfBirth: formatDateOfBirth(user.dateOfBirth) ?? undefined,
    ...(user.sexAtBirth && { sexAtBirth: user.sexAtBirth }),
    mobilePhone: user.phoneNumber
      ? formatStoredPhoneNumber(user.phoneNumber)
      : undefined,
  };
};

type PatientInfoStepProps = {
  userId: string | null;
  patient: User | undefined;
  email: string;
  visitType: VisitType;
  stateId: number;
  insuranceId: number | null;
  appointmentDetails: AppointmentDetails;
  patientInfo: Partial<StaffPatientInfoFormData> | null;
  onBack: (values: Partial<StaffPatientInfoFormData>) => void;
  onChooseAnotherSlot: (values: Partial<StaffPatientInfoFormData>) => void;
  onBookingChange: (isBooking: boolean) => void;
  onBooked: (response: AdminScheduleVisitResponse) => void;
};

export const PatientInfoStep = ({
  userId,
  patient,
  email,
  visitType,
  stateId,
  insuranceId,
  appointmentDetails,
  patientInfo,
  onBack,
  onChooseAnotherSlot,
  onBookingChange,
  onBooked,
}: PatientInfoStepProps) => {
  const isMounted = useRef(true);
  const bookingFlow = useBookingFlowStore((state) => state.flow);
  const { trackTimeEvent } = useBookingFlowEvents();
  const { mutate: updateBookingFlow } = useUpdateBookingFlowMutation();

  const [bookingError, setBookingError] = useState<BookingError | null>(null);
  const [fieldErrors, setFieldErrors] = useState<StaffPatientInfoFieldErrors>(
    {},
  );
  const [hasFileSizeError, setHasFileSizeError] = useState(false);

  useEffect(() => {
    trackTimeEvent(BOOKING_FLOW_EVENTS.PATIENT_INFO_PAGE_VIEWED_AT);
  }, []);

  const hasEhrId = !!patient?.ehrId;
  const hasPlan =
    insuranceId !== null && insuranceId !== INSURANCE_NOT_FOUND_OPTION.value;

  const defaultValues: Partial<StaffPatientInfoFormData> = patientInfo ?? {
    ...(visitType === VISIT_TYPES.FOLLOW_UP && {
      fillAddressInfo: FILL_OPTION.LATER,
      fillInsuranceInfo: FILL_OPTION.LATER,
    }),
    ...getAccountPrefill(patient),
  };

  const { mutate: bookVisit, isPending } = useStaffBookVisit({
    onMutate: () => onBookingChange(true),
    onSettled: () => onBookingChange(false),
    onSuccess: (response) => {
      if (isMounted.current) onBooked(response);
    },
    onError: (error) => {
      const parsed = parseBookingError(error);

      setHasFileSizeError(
        isAxiosError(error) &&
          error.response?.status === HttpStatusCode.PayloadTooLarge,
      );
      setFieldErrors(parsed.fieldErrors);
      setBookingError(parsed.bookingError);
    },
  });

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      onBookingChange(false);
    };
  }, []);

  const handleHiddenFieldErrors = (messages: string[]) =>
    setBookingError((current) => ({
      type: "fields",
      messages: [
        ...(current?.type === "fields" ? current.messages : []),
        ...messages,
      ],
    }));

  const handleSubmit = (data: StaffPatientInfoFormData) => {
    const providerId = appointmentDetails.provider?.id;
    if (!providerId) return;

    setBookingError(null);
    setHasFileSizeError(false);

    if (
      bookingFlow &&
      visitType === VISIT_TYPES.INITIAL &&
      data.fillInsuranceInfo
    ) {
      updateBookingFlow({
        id: bookingFlow.id,
        [BOOKING_FLOW_EVENTS.ENTER_INSURANCE_INFO]: data.fillInsuranceInfo,
      });
    }

    bookVisit({
      visitData: buildStaffBookingPayload(data, {
        userId,
        email,
        hasEhrId,
        visitType,
        stateId,
        insuranceId,
        providerId,
        visitDateTime: appointmentDetails.visitDateTime,
        bookingFlowId: bookingFlow?.id,
      }),
    });
  };

  return (
    <div className="flex min-h-full flex-col gap-6">
      <StepHeading title="Book a visit" visitType={visitType} />

      <AppointmentDetailsCard
        appointmentDetails={appointmentDetails}
        hideContactInfo
        size="compact"
      />

      <PatientInfoForm
        visitType={visitType}
        hasEhrId={hasEhrId}
        hasPlan={hasPlan}
        stateId={stateId}
        defaultValues={defaultValues}
        fieldErrors={fieldErrors}
        hasFileSizeError={hasFileSizeError}
        isLoading={isPending}
        errorBanner={(chooseAnotherSlot) =>
          bookingError && (
            <BookingErrorAlert
              error={bookingError}
              onChooseAnotherSlot={chooseAnotherSlot}
            />
          )
        }
        onSubmit={handleSubmit}
        onBack={onBack}
        onChooseAnotherSlot={onChooseAnotherSlot}
        onHiddenFieldErrors={handleHiddenFieldErrors}
      />
    </div>
  );
};

const BookingErrorAlert = ({
  error,
  onChooseAnotherSlot,
}: {
  error: BookingError;
  onChooseAnotherSlot: () => void;
}) => {
  if (error.type === "slotUnavailable") {
    return (
      <NotificationBanner variant="error" title="The visit was not booked">
        <p>{error.message} The information entered is kept.</p>
        <ChooseAnotherSlotButton onClick={onChooseAnotherSlot} />
      </NotificationBanner>
    );
  }

  if (error.type === "blocking") {
    return (
      <NotificationBanner variant="error" title="The visit was not booked">
        <p>{error.message}</p>
      </NotificationBanner>
    );
  }

  if (error.type === "fields") {
    return (
      <NotificationBanner variant="error" title="The visit was not booked">
        <ul className="list-disc pl-4">
          {error.messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </NotificationBanner>
    );
  }

  return (
    <NotificationBanner variant="error" title="The visit was not booked">
      <p>{error.message}</p>
    </NotificationBanner>
  );
};
